import type { IDocumentRepository, PaginatedResponse, QueryParams } from '@yawi/domain';
import { buildQueryString, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@yawi/domain';
import type { CommunityDocument, DocumentCategory } from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

// Tipos de respuesta de la API
interface ApiDocumentResponse {
  id: string;
  title: string;
  category: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  isPublic: boolean;
  uploadedBy: string;
  createdAt: string;
  updatedAt: string;
  fileId?: string; // ID del archivo en el sistema de archivos
  sectionIds?: string[];
}

interface ApiCreateDocumentRequest {
  title: string;
  category: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  isPublic?: boolean;
  fileId?: string; // ID del archivo para presigned URLs
  sectionIds?: string[];
}

// Helper para inferir mimeType de la URL
const getMimeTypeFromUrl = (url: string): string => {
  const ext = url.split('.').pop()?.toLowerCase().split('?')[0];
  const mimeMap: Record<string, string> = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    mp4: 'video/mp4',
    mp3: 'audio/mpeg',
  };
  return mimeMap[ext || ''] || 'application/octet-stream';
};

// Mapea la respuesta de la API al tipo CommunityDocument interno
const mapApiDocumentToDocument = (apiDocument: ApiDocumentResponse): CommunityDocument => ({
  id: apiDocument.id,
  title: apiDocument.title,
  category: apiDocument.category as DocumentCategory,
  uploadDate: apiDocument.createdAt,
  url: apiDocument.fileUrl,
  size: `${(apiDocument.fileSize / 1024).toFixed(2)} KB`,
  uploadedBy: apiDocument.uploadedBy,
  fileId: apiDocument.fileId,
  mimeType: apiDocument.mimeType,
  sectionIds: apiDocument.sectionIds ?? [],
});

export class ApiDocumentRepository implements IDocumentRepository {
  async getAllDocuments(): Promise<CommunityDocument[]> {
    const client = getApiClient();
    const response = await client.get<PaginatedResponse<ApiDocumentResponse>>('v1/documents');
    return response.data.map(mapApiDocumentToDocument);
  }

  async getDocuments(params?: QueryParams): Promise<PaginatedResponse<CommunityDocument>> {
    const client = getApiClient();
    const queryString = buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      search: params?.search,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      ...params,
    });
    const response = await client.get<PaginatedResponse<ApiDocumentResponse>>(
      `v1/documents?${queryString}`
    );
    return {
      ...response,
      data: response.data.map(mapApiDocumentToDocument),
    };
  }

  async uploadDocument(doc: Omit<CommunityDocument, 'id' | 'uploadDate'>): Promise<CommunityDocument> {
    const client = getApiClient();

    // Convertir size string a número (asumiendo formato "XXX KB" o "XXX MB")
    let fileSize = 0;
    if (doc.size) {
      const sizeStr = doc.size.toLowerCase();
      const value = parseFloat(doc.size);
      if (sizeStr.includes('mb')) {
        fileSize = value * 1024 * 1024;
      } else if (sizeStr.includes('kb')) {
        fileSize = value * 1024;
      } else {
        fileSize = value;
      }
    }

    // Detectar mimeType desde la URL si no viene
    const mimeType = doc.mimeType || getMimeTypeFromUrl(doc.url);

    const request: ApiCreateDocumentRequest = {
      title: doc.title,
      category: doc.category,
      fileUrl: doc.url,
      fileSize: Math.round(fileSize),
      mimeType,
      isPublic: true,
      fileId: doc.fileId, // Incluir fileId para presigned URLs
      sectionIds: doc.sectionIds,
    };
    const apiDocument = await client.post<ApiDocumentResponse>('v1/documents', request);
    return mapApiDocumentToDocument(apiDocument);
  }

  async deleteDocument(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/documents/${id}`);
  }

  async getDocumentById(id: string): Promise<CommunityDocument | null> {
    const client = getApiClient();
    try {
      const apiDocument = await client.get<ApiDocumentResponse>(`v1/documents/${id}`);
      return mapApiDocumentToDocument(apiDocument);
    } catch {
      return null;
    }
  }

  async updateDocument(document: CommunityDocument): Promise<CommunityDocument> {
    const client = getApiClient();
    const request = {
      title: document.title,
      category: document.category,
      isPublic: true, // Por defecto público
      sectionIds: document.sectionIds,
    };
    const apiDocument = await client.put<ApiDocumentResponse>(`v1/documents/${document.id}`, request);
    return mapApiDocumentToDocument(apiDocument);
  }

  async getCategories(): Promise<string[]> {
    const client = getApiClient();
    return await client.get('v1/documents/categories');
  }

  async getDocumentsByCategory(category: string): Promise<CommunityDocument[]> {
    const client = getApiClient();
    const response = await client.get<PaginatedResponse<ApiDocumentResponse>>(`v1/documents/category/${category}`);
    return response.data.map(mapApiDocumentToDocument);
  }
}
